import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { MultiSearchableSelect } from '../../ui/MultiSearchableSelect';

/**
 * The filter row of /strategic-kpis (masterplan skpi-redesign §4 «Filter», mockup «filters»; package A2a):
 * one `.field` per filter — label above, value below, chevron — in the order the page passes them, with
 * `{ kind: 'sep' }` for the thin divider between the frame (BU, year, as-of date) and the selection lists.
 *
 * The controls stay the native ones and MultiSearchableSelect stays unchanged: FilterRow only puts the
 * housing around them, and `kit.css` §5a dresses the inside (an invisible select over the field, a
 * borderless date input, the multi-select's trigger stretched over the field). A field that differs from
 * «all» is `.set` (ultramarine), as in the sales app.
 *
 * No data logic: values, options and handlers come in as props. `anchorClassName` / `anchorRef` let the
 * page put the What's-New anchor `filter:skpi-selection` (§2 rule 12) on the row.
 */

export interface FilterOption {
    value: string;
    label: string;
}

export type FilterSpec =
    | {
        kind: 'select';
        key: string;
        label: string;
        value: string;
        options: readonly FilterOption[];
        onChange: (value: string) => void;
        /** The field counts as set (ultramarine) — e.g. a BU other than the default. */
        set?: boolean;
        /** A small mark before the value, such as the BU swatch `<span className="bu spc" />`. */
        lead?: ReactNode;
        testId?: string;
    }
    | {
        kind: 'date';
        key: string;
        label: string;
        /** yyyy-mm-dd */
        value: string;
        onChange: (value: string) => void;
        set?: boolean;
        testId?: string;
    }
    | {
        kind: 'multi';
        key: string;
        label: string;
        options: readonly { id: string; name: string }[];
        values: string[];
        onChange: (values: string[]) => void;
        /** What the field says with nothing chosen; default «All (n)». */
        allText?: string;
        /** A marker beside the label (What's New `NewMarker`). */
        marker?: ReactNode;
        testId?: string;
    }
    | { kind: 'sep'; key: string };

interface Props {
    fields: readonly FilterSpec[];
    /** After the fields: the Refresh button, the «Computing…» status. */
    trailing?: ReactNode;
    /** Extra class on the row (the What's-New spotlight class). */
    anchorClassName?: string;
    anchorRef?: React.Ref<HTMLDivElement>;
    ariaLabel?: string;
    testId?: string;
}

/** The value a multi field shows: «All (n)», the one name, or «n selected». */
export function multiValueText(values: readonly string[], options: readonly { id: string; name: string }[], allText?: string): string {
    if (values.length === 0) return allText ?? `All (${options.length})`;
    if (values.length === 1) return options.find(o => o.id === values[0])?.name ?? values[0];
    return `${values.length} selected`;
}

const fieldId = (key: string) => `skpi-filter-${key}`;

export function FilterRow({ fields, trailing, anchorClassName, anchorRef, ariaLabel = 'Filters', testId }: Props) {
    return (
        <div ref={anchorRef} className={`filters${anchorClassName ? ` ${anchorClassName}` : ''}`} role="group" aria-label={ariaLabel} data-testid={testId}>
            {fields.map(f => {
                if (f.kind === 'sep') return <span key={f.key} className="fsep" aria-hidden="true" />;
                const id = fieldId(f.key);
                if (f.kind === 'select') {
                    const current = f.options.find(o => o.value === f.value)?.label ?? f.value;
                    return (
                        <div key={f.key} className={`field native${f.set ? ' set' : ''}`}>
                            <small id={`${id}-label`}>{f.label}</small>
                            <b>{f.lead}{current}</b>
                            <span className="chev"><ChevronDown className="i sm" aria-hidden="true" /></span>
                            <select
                                id={id}
                                className="field-native"
                                value={f.value}
                                onChange={e => f.onChange(e.target.value)}
                                aria-labelledby={`${id}-label`}
                                data-testid={f.testId}
                            >
                                {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                    );
                }
                if (f.kind === 'date') {
                    return (
                        <div key={f.key} className={`field${f.set ? ' set' : ''}`}>
                            <small><label htmlFor={id}>{f.label}</label></small>
                            <input
                                id={id}
                                type="date"
                                className="field-date"
                                value={f.value}
                                onChange={e => f.onChange(e.target.value)}
                                data-testid={f.testId}
                            />
                        </div>
                    );
                }
                return (
                    <div key={f.key} className={`field ms${f.values.length > 0 ? ' set' : ''}`} data-testid={f.testId}>
                        <small>{f.label}{f.marker}</small>
                        <b>{multiValueText(f.values, f.options, f.allText)}</b>
                        <span className="chev"><ChevronDown className="i sm" aria-hidden="true" /></span>
                        <MultiSearchableSelect
                            id={id}
                            className="ms-host"
                            options={[...f.options]}
                            values={f.values}
                            onChange={f.onChange}
                            placeholder={multiValueText([], f.options, f.allText)}
                            ariaLabel={f.label}
                            maxChips={0}
                        />
                    </div>
                );
            })}
            {trailing}
        </div>
    );
}
